import { useState } from 'react';
import { useTranslations } from 'next-intl';
import { HStack, Text, Button } from '@chakra-ui/react';

import type { HexString } from '@/lib/config/wagmi';
import { useHLClearingHouseState } from '@/lib/hooks/useMargin';
import { useHLOpenOrders } from '@/lib/hooks/useOpenOrders';

interface TabItem {
  id: string;
  label: string;
  count?: number;
}

interface PortfolioTabsProps {
  onTabChange?: (tabId: string) => void;
  initialTab?: string;
  userAddress: HexString;
}

export function PortfolioTabs({
  onTabChange,
  initialTab = 'positions',
  userAddress,
}: PortfolioTabsProps) {
  const t = useTranslations('trader.tabs');
  const [activeTab, setActiveTab] = useState(initialTab);

  const {
    data: clearingHouseState,
    isLoading,
    error,
  } = useHLClearingHouseState({ user: userAddress });

  const {
    data: openOrders,
    isLoading: isLoadingOpenOrders,
    error: errorOpenOrders,
  } = useHLOpenOrders(userAddress);

  const openOrdersCount = openOrders?.length || 0;

  // Filter out positions with zero size
  const activePositions =
    clearingHouseState?.assetPositions.filter(
      (assetPosition) => Number.parseFloat(assetPosition.position.szi) !== 0,
    ) || [];

  const tabs: TabItem[] = [
    {
      id: 'positions',
      label: t('position'),
      count: activePositions.length,
    },
    { id: 'history', label: t('history') },
    { id: 'orders', label: t('orders'), count: openOrdersCount },
    { id: 'depositsWithdrawals', label: t('depositsWithdrawals') },
  ];

  const handleTabChange = (tabId: string) => {
    setActiveTab(tabId);
    onTabChange?.(tabId);
  };

  return (
    <HStack
      gap={4}
      px={4}
      align="center"
      w="full"
      overflowX="auto"
      scrollbar="hidden"
    >
      {tabs.map((tab) => {
        const isActive = activeTab === tab.id;
        const displayLabel =
          tab.count !== undefined ? `${tab.label} (${tab.count})` : tab.label;

        return (
          <Button
            key={tab.id}
            onClick={() => handleTabChange(tab.id)}
            variant="ghost"
            size="sm"
            bg={isActive ? 'text.t1/15' : 'transparent'}
            rounded="full"
            px={2}
            py={2}
          >
            <Text
              fontSize="xs"
              fontWeight="medium"
              color={isActive ? 'foreground/90' : 'foreground/60'}
            >
              {displayLabel}
            </Text>
          </Button>
        );
      })}
    </HStack>
  );
}
